import React, { useEffect, useContext, useState, useRef } from 'react';
import { FiUpload, FiDownload, FiTrash2 } from 'react-icons/fi';
import { BiCheckCircle, BiSend, BiXCircle, BiX } from 'react-icons/bi';
import { WebsiteFileContext } from '../../context/WebsiteFileProvider';
import { VendorFileContext } from '../../context/VendorFileProvider';
import { UserContext } from '../../context/UserProvider';
import { WebsiteContext } from '../../context/WebsiteProvider';
import { VendorContext } from '../../context/VendorProvider';
import FileModal from '../../components/modals/FileModal';
import DeleteModal from '../../components/modals/DeleteModal';
import ConfirmModal from '../../components/modals/ConfirmModal';
import SuccessNotification from '../../components/toast/SuccessNotification';
import ErrorNotification from '../../components/toast/ErrorNotification';
import ExportButtonLarge from '../../components/buttons/ExportButtonLarge';
import DocuSignStatusBar from '../../components/display/DocuSignStatusBar';
import { FaExclamationCircle, FaBan, FaFileSignature } from 'react-icons/fa';
import { IoIosCheckmarkCircle } from 'react-icons/io';

export default function WebsiteFiles({ websiteId }) {
  const {
    websiteFiles,
    fileTypes,
    getFileTypes,
    handleFileUpload,
    deleteFile,
    approveDocument,
    downloadFile,
    getWebsiteFiles,
    formatDate,
    formatFileSize,
    getDocuSignStatus,
    resendDocument,
    voidDocument,
    sendDocusign,
    getDocuSignDetails,
  } = useContext(WebsiteFileContext);
  const { getVendorFiles } = useContext(VendorFileContext);
  const { vendor } = useContext(VendorContext);
  const { updateWebsiteField } = useContext(WebsiteContext);
  const { isExpanded } = useContext(UserContext);
  const [selectedFileType, setSelectedFileType] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [isBrowseOpen, setIsBrowseOpen] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fileIdToDelete, setFileIdToDelete] = useState(null);
  const [fileIdToConfirm, setFileIdToConfirm] = useState(null);
  const [selectedDocTypeId, setSelectedDocTypeId] = useState('');
  const [isDocuSignModalOpen, setIsDocuSignModalOpen] = useState(false);
  const [agentSignatory, setAgentSignatory] = useState(null);
  const [brokerSignatory, setBrokerSignatory] = useState(null);
  const [loadingSignatories, setLoadingSignatories] = useState(false);
  const [showDocuSignConfirmModal, setShowDocuSignConfirmModal] = useState(false);
  const isUploadButtonVisible = selectedFile && selectedFileType;
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [text, setText] = useState('');
  const [showDocuSignStatus, setShowDocuSignStatus] = useState(false);
  const docuSignRef = useRef(null);
  const [docuSignStatusRowKey, setDocuSignStatusRowKey] = useState(null);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [voidReason, setVoidReason] = useState('');

  const completedModal = () => {
    setShowConfirmModal(true);
  };

  const closeCompletedModal = () => {
    setShowConfirmModal(false);
  };

  const openVoidModal = () => {
    setShowVoidModal(true);
  };

  const closeVoidModal = () => {
    setShowVoidModal(false);
  };

  const toggleSelectDropdown = () => {
    setIsSelectOpen(!isSelectOpen);
  };

  const openDocuSignModal = async () => {
    setIsDocuSignModalOpen(true);
    setLoadingSignatories(true);
    try {
      const docTypeId =
        selectedDocTypeId ||
        fileTypes.find((type) => type.signatureRequired)?.id ||
        35;
      const response = await getDocuSignDetails(docTypeId, websiteId);
      const signatures = Array.isArray(response) ? response : response.signatures || [];
      const agent = signatures.find((s) => s.signatory.toLowerCase() === 'agent');
      const broker = signatures.find((s) => s.signatory.toLowerCase() === 'broker');
      setAgentSignatory(agent || null);
      setBrokerSignatory(broker || null);
    } catch (error) {
      setShowError(true);
      setText('Failed to load signatory details.');
    } finally {
      setLoadingSignatories(false);
    }
  };

  const closeDocuSignModal = () => {
    setIsDocuSignModalOpen(false);
    setSelectedDocTypeId('');
    setAgentSignatory(null);
    setBrokerSignatory(null);
  };

  const closeNotification = () => {
    setShowSuccess(false);
    setShowError(false);
  };

  useEffect(() => {
    if (websiteId) {
      getWebsiteFiles(websiteId);
    }
  }, [websiteId]);

  useEffect(() => {
    getFileTypes();
  }, []);

  useEffect(() => {
    const docTypes = fileTypes.filter((type) => type.signatureRequired);
    if (docTypes.length === 1) {
      setSelectedDocTypeId(docTypes[0].id.toString());
    }
  }, [fileTypes]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile || !selectedFileType) return;
    const allowedFileTypes = [
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/plain',
      'image/png',
      'image/gif',
      'image/jpeg',
      'image/webp',
      'application/pdf',
      'application/vnd.oasis.opendocument.text',
      'application/vnd.oasis.opendocument.spreadsheet-template',
    ];
    const fileType = selectedFile.type;
    if (!allowedFileTypes.includes(fileType)) {
      setShowError(true);
      setText('Invalid file type. Only certain file types are allowed.');
      return;
    }
    const fileSizeInBytes = selectedFile.size;
    const maxSizeInBytes = 10 * 1024 * 1024;
    if (fileSizeInBytes > maxSizeInBytes) {
      setShowError(true);
      setText('File size exceeds the limit of 10MB.');
      return;
    }
    const formData = new FormData();
    formData.append('website', websiteId);
    formData.append('file', selectedFile);
    formData.append('documentType', selectedFileType);
    try {
      await handleFileUpload(formData, websiteId);
      closeBrowseModal();
      setShowSuccess(true);
      setText('File submitted successfully');
      if (vendor.rowKey) {
        await getVendorFiles(vendor.rowKey);
      }
    } catch (error) {
      setShowError(true);
      setText(`${error.message}`);
    }
  };

  const handleFileTypeChange = (e) => {
    setSelectedFileType(e.target.value);
  };

  const handleDocTypeChange = async (e) => {
    const newDocTypeId = e.target.value;
    setSelectedDocTypeId(newDocTypeId);
    if (newDocTypeId) {
      setLoadingSignatories(true);
      try {
        const response = await getDocuSignDetails(newDocTypeId, websiteId);
        const signatures = Array.isArray(response) ? response : response.signatures || [];
        const agent = signatures.find((s) => s.signatory.toLowerCase() === 'agent');
        const broker = signatures.find((s) => s.signatory.toLowerCase() === 'broker');
        setAgentSignatory(agent || null);
        setBrokerSignatory(broker || null);
      } catch (error) {
        setShowError(true);
        setText('Failed to load signatory details for selected document type.');
      } finally {
        setLoadingSignatories(false);
      }
    } else {
      setAgentSignatory(null);
      setBrokerSignatory(null);
    }
  };

  const handleAgentChange = (field, value) => {
    setAgentSignatory((prev) => ({ ...prev, [field]: value }));
  };

  const handleBrokerChange = (field, value) => {
    setBrokerSignatory((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleDownload = (file) => {
    downloadFile(file);
  };

  const openBrowseModal = () => {
    setIsBrowseOpen(true);
  };

  const closeBrowseModal = () => {
    setSelectedFileType('');
    setSelectedFile(null);
    setIsDraggingOver(false);
    setIsBrowseOpen(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDraggingOver(true);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    setIsDraggingOver(true);
  };

  const handleDragLeave = () => {
    setIsDraggingOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDraggingOver(false);
    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) {
      setSelectedFile(droppedFiles[0]);
    }
  };

  const handleDeleteComment = (fileId) => {
    setFileIdToDelete(fileId);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirmation = async () => {
    try {
      await deleteFile(fileIdToDelete);
      setShowDeleteModal(false);
      await getWebsiteFiles(websiteId);
      if (vendor.rowKey) {
        await getVendorFiles(vendor.rowKey);
      }
    } catch (error) {
      setShowError(true);
      setText(`Error deleting file: ${error.message}`);
    }
  };

  const handleConfirmation = async (rowKey) => {
    try {
      await approveDocument(rowKey);
      const hasPendingDocuments = websiteFiles.some(
        (file) => file.rowKey !== rowKey && file.documentsWaiting
      );
      updateWebsiteField(websiteId, { documentsWaiting: hasPendingDocuments });
      setShowConfirmModal(false);
    } catch (error) {
      setShowError(true);
      setText(`Error confirming document: ${error.message}`);
    }
  };

  const handleResendConfirmation = async (rowKey) => {
    try {
      await resendDocument(rowKey);
      setShowConfirmModal(false);
      setShowSuccess(true);
      setText('Document resent successfully');
    } catch (error) {
      setShowError(true);
      setText(`Error resending document: ${error.message}`);
    }
  };

  const handleVoidConfirmation = async () => {
    if (!voidReason.trim()) {
      setShowError(true);
      setText('Please provide a reason for voiding the document.');
      return;
    }
    try {
      await voidDocument(fileIdToConfirm, voidReason);
      closeVoidModal();
      setShowSuccess(true);
      setText('Document voided successfully');
    } catch (error) {
      setShowError(true);
      setText(`Error voiding document: ${error.message}`);
    }
  };

  const openDocuSignConfirmModal = () => {
    setShowDocuSignConfirmModal(true);
  };

  const closeDocuSignConfirmModal = () => {
    setShowDocuSignConfirmModal(false);
  };

  const handleSendDocusign = async () => {
    if (!selectedDocTypeId) {
      setShowError(true);
      setText('Please select a document type.');
      return;
    }
    openDocuSignConfirmModal();
  };

  const confirmSendDocusign = async () => {
    setLoadingSignatories(true);
    try {
      const payload = {
        id: websiteId,
        signatures: [],
        type: 'website',
      };
      if (agentSignatory) {
        payload.signatures.push({
          anchor: agentSignatory.anchor,
          canOverride: agentSignatory.canOverride,
          dateAnchor: agentSignatory.dateAnchor,
          email: agentSignatory.email,
          emailAnchor: agentSignatory.emailAnchor,
          label: agentSignatory.label,
          name: agentSignatory.name,
          nameAnchor: agentSignatory.nameAnchor,
          signatory: agentSignatory.signatory,
        });
      }
      if (brokerSignatory) {
        payload.signatures.push({
          anchor: brokerSignatory.anchor,
          canOverride: brokerSignatory.canOverride,
          dateAnchor: brokerSignatory.dateAnchor,
          email: brokerSignatory.email,
          emailAnchor: brokerSignatory.emailAnchor,
          label: brokerSignatory.label,
          name: brokerSignatory.name,
          nameAnchor: brokerSignatory.nameAnchor,
          signatory: brokerSignatory.signatory,
        });
      }
      await sendDocusign(selectedDocTypeId, payload);
      closeDocuSignModal();
      closeDocuSignConfirmModal();
      setShowSuccess(true);
      setText('Document sent successfully');
    } catch (error) {
      setShowError(true);
      setText('DocuSign send failed. Please contact support.');
    } finally {
      setLoadingSignatories(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (docuSignRef.current && !docuSignRef.current.contains(event.target)) {
        setDocuSignStatusRowKey(null);
        setShowDocuSignStatus(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleDocuSignStatus = (rowKey) => {
    if (docuSignStatusRowKey === rowKey) {
      setDocuSignStatusRowKey(null);
      setShowDocuSignStatus(false);
    } else {
      setDocuSignStatusRowKey(rowKey);
      setShowDocuSignStatus(true);
    }
  };

  const exportData = websiteFiles.map((file) => ({
    fileName: file.label,
    fileType:
      fileTypes.find((fileType) => fileType.id === file.type)?.type || 'N/A',
    uploadedBy: file.uploadedBy,
    size: formatFileSize(file.size),
    date: formatDate(file.uploadDate),
  }));

  useEffect(() => {
    if (docuSignStatusRowKey) {
      getDocuSignStatus(docuSignStatusRowKey);
    }
  });

  return (
    <div className="modal mb-12 mt-3 flex h-full flex-col">
      <div className="modal-content flex-grow p-4">
        {websiteFiles.length === 0 ? (
          <div className="mb-6 mt-6 flex w-1/3 flex-col items-center justify-center space-y-4 rounded-md bg-gray-100 py-32 text-center text-gray-500 dark:bg-stone-800 dark:text-gray-200 sm:w-1/3 md:w-1/2 lg:mx-auto lg:w-3/4">
            <p>No Files Added.</p>
            <div className="flex items-center space-x-4">
              <label className="flex w-40 cursor-pointer items-center rounded bg-blue-600 px-3 py-2 text-white font-semibold transition-colors duration-200 hover:bg-blue-700 dark:bg-indigo-600 dark:hover:bg-indigo-700">
                <FiUpload className="mr-2" />
                <span>Upload File</span>
                <input onClick={openBrowseModal} className="hidden" />
              </label>
              <button
                onClick={openDocuSignModal}
                className="flex w-40 cursor-pointer items-center rounded bg-blue-600 px-3 py-2 text-white font-semibold transition-colors duration-200 hover:bg-blue-700 dark:bg-indigo-600 dark:hover:bg-indigo-700"
              >
                <FaFileSignature className="mr-2" />
                <span>E-Sign</span>
              </button>
            </div>
          </div>
        ) : (
          <div>
            {!isBrowseOpen && (
              <div className="mb-4 flex items-center space-x-4">
                <label className="flex min-w-[130px] w-lg cursor-pointer items-center rounded bg-blue-600 px-3 py-2 text-white font-semibold transition-colors duration-200 hover:bg-blue-700 dark:bg-indigo-500 dark:hover:bg-indigo-600">
                  <FiUpload className="mr-2" />
                  <span>Upload File</span>
                  <input onClick={openBrowseModal} className="hidden" />
                </label>
                <ExportButtonLarge excelData={exportData} fileName={'WebsiteFiles'} />
                <button
                  onClick={openDocuSignModal}
                  className="flex min-w-[130px] w-lg cursor-pointer items-center rounded bg-blue-600 px-3 py-2 text-white font-semibold transition-colors duration-200 hover:bg-blue-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
                >
                  <FaFileSignature className="mr-2" />
                  <span>E-Sign</span>
                </button>
              </div>
            )}
            <div className={`${isExpanded ? 'h-[650px]' : 'h-[500px]'} overflow-y-auto`}>
              <table className="mt-6 border-collapse text-left text-gray-600 dark:text-gray-300">
                <thead className="text-xs">
                <tr>
                  <th className="w-1/4 truncate px-4 py-2 uppercase">File Name</th>
                  <th className="w-1/5 px-4 py-2 uppercase">File Types</th>
                  <th className="w-1/4 px-4 py-2 uppercase lg:table-cell">Uploaded By</th>
                  <th className="w-1/4 px-4 py-2 uppercase lg:table-cell">Size</th>
                  <th className="w-1/4 px-4 py-2 uppercase lg:table-cell">Date</th>
                  <th className="w-1/4 px-4 py-2 uppercase">Actions</th>
                </tr>
                </thead>
                <tbody>
                {websiteFiles.map((file, index) => (
                  <tr
                    key={index}
                    className="border hover:bg-gray-100 dark:border-stone-700 dark:bg-stone-800 dark:hover:bg-stone-700"
                  >
                    <td className="truncate px-2 py-2" title={file.label}>
                      {file.label.length > 20
                        ? file.label.substr(0, 20) + '...'
                        : file.label}
                    </td>
                    <td className="w-10 truncate px-2 py-2" title={file.type}>
                      {file.type
                        ? fileTypes.find((fileType) => fileType.id === file.type)?.type
                          ?.length > 20
                          ? fileTypes
                          .find((fileType) => fileType.id === file.type)
                          ?.type?.substr(0, 20) + '...'
                          : fileTypes.find((fileType) => fileType.id === file.type)
                            ?.type
                        : 'N/A'}
                    </td>
                    <td
                      className="truncate px-2 py-2 lg:table-cell"
                      title={file.uploadedBy ? file.uploadedBy : 'Deleted User'}
                    >
                      {file.uploadedBy && file.uploadedBy !== 'Deleted User' ? (
                        file.uploadedBy
                      ) : (
                        <span className="font-bold text-red-500">Deleted User</span>
                      )}
                    </td>
                    <td className="w-20 truncate px-2 py-2 lg:table-cell">
                      {formatFileSize(file.size)}
                    </td>
                    <td className="w-20 truncate px-2 py-2 lg:table-cell">
                      {formatDate(file.uploadDate)}
                    </td>
                    <td className="flex items-center px-2 py-2">
                      <button
                        onClick={() => handleDownload(file)}
                        className="flex items-center rounded px-2 py-2 font-semibold text-blue-500 hover:text-blue-400 dark:text-indigo-500 dark:hover:text-indigo-600"
                      >
                        <FiDownload />
                      </button>
                      <button
                        onClick={() => handleDeleteComment(file.rowKey)}
                        className="flex items-center rounded px-2 py-2 font-semibold text-red-500 hover:text-red-400"
                      >
                        <FiTrash2 />
                      </button>
                      <div className="relative mt-1">
                        <button
                          onClick={() => toggleDocuSignStatus(file.rowKey)}
                          className="ml-1"
                        >
                          {file.signatureRequired ? (
                            file.voided ? (
                              <FaBan
                                title="Voided"
                                className="ml-1 h-4 w-4 text-gray-500 dark:text-gray-500"
                              />
                            ) : file.approved ? (
                              <IoIosCheckmarkCircle
                                title="Completed"
                                className="mr-2 h-5 w-5 text-green-500 dark:text-green-500"
                              />
                            ) : (
                              <FaExclamationCircle
                                title="Pending Approval"
                                className="mr-2 h-4 w-4 text-orange-400 dark:text-yellow-400"
                              />
                            )
                          ) : null}
                        </button>
                        {docuSignStatusRowKey === file.rowKey && showDocuSignStatus && (
                          <div
                            ref={docuSignRef}
                            className="absolute right-0 z-50 mt-2 w-auto min-w-[300px] rounded-md border border-gray-300 bg-white p-4 shadow-lg dark:border-stone-700 dark:bg-stone-900"
                          >
                            <p className="text-md mb-2 ml-1 font-semibold">
                              Document Progress
                            </p>
                            <div className="mb-1">
                              <DocuSignStatusBar fileId={file.rowKey} />
                            </div>
                            {!file.approved && (
                              <div className="mt-3 flex space-x-4">
                                <button
                                  onClick={() => {
                                    setFileIdToConfirm(file.rowKey);
                                    completedModal();
                                  }}
                                  className="flex cursor-pointer items-center text-green-600 dark:text-green-400"
                                >
                                  <BiCheckCircle className="mr-1 h-5 w-5" />
                                  <span className="text-sm font-bold">Approve</span>
                                </button>
                                <button
                                  onClick={() => handleResendConfirmation(file.rowKey)}
                                  className="flex cursor-pointer items-center text-blue-600 dark:text-indigo-400"
                                >
                                  <BiSend className="mr-1 h-5 w-5" />
                                  <span className="text-sm font-bold">Resend</span>
                                </button>
                                <button
                                  onClick={() => {
                                    if (!file.voided) {
                                      setFileIdToConfirm(file.rowKey);
                                      openVoidModal();
                                    }
                                  }}
                                  className={`flex items-center text-red-600 dark:text-red-400 ${
                                    file.voided
                                      ? 'cursor-not-allowed opacity-50'
                                      : 'cursor-pointer'
                                  }`}
                                  disabled={file.voided}
                                >
                                  <BiXCircle className="mr-1 h-5 w-5" />
                                  <span className="text-sm font-bold">
                                      {file.voided ? 'Voided' : 'Void'}
                                    </span>
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {showSuccess && (
          <SuccessNotification
            show={showSuccess}
            onClose={closeNotification}
            text={text}
          />
        )}
        {showError && (
          <ErrorNotification
            show={showError}
            onClose={closeNotification}
            text={text}
          />
        )}
      </div>
      {isBrowseOpen && (
        <FileModal
          isOpen={isBrowseOpen}
          onCancel={closeBrowseModal}
          onSubmit={handleFileUpload}
          selectedFileType={selectedFileType}
          handleFileTypeChange={handleFileTypeChange}
          fileTypes={fileTypes}
        >
          <form onSubmit={handleSubmit}>
            <div className="m-14">
              <div
                className={`flex items-center space-x-4 border-2 border-dashed ${
                  isDraggingOver
                    ? 'border-indigo-600'
                    : 'border-sky-300 dark:border-indigo-300'
                } rounded-md p-28`}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <label className="flex w-28 cursor-pointer items-center rounded bg-blue-600 px-3 py-2 text-white font-semibold transition-colors duration-200 hover:bg-blue-700 dark:bg-indigo-600 dark:hover:bg-indigo-700">
                  <FiUpload className="mr-2" />
                  <span>Browse</span>
                  <input
                    type="file"
                    onChange={handleFileChange}
                    className="hidden"
                    id={`file-type-input-${selectedFile ? selectedFile.name : 'default'}`}
                    disabled={isDraggingOver}
                    onClick={toggleSelectDropdown}
                  />
                </label>
                <div className="flex flex-col items-start space-y-2">
                  <select
                    id="file-type-input"
                    className="mt-8 block w-48 rounded-md border border-gray-300 bg-gray-100 py-2.5 pl-2 text-gray-700 dark:border-stone-700 dark:bg-stone-700 dark:text-gray-300"
                    name="status"
                    type="text"
                    value={selectedFileType}
                    onChange={handleFileTypeChange}
                  >
                    <option value="">Select File Type</option>
                    {fileTypes.map((type) => (
                      <option value={type.id} key={type.id}>
                        {type.type}
                      </option>
                    ))}
                  </select>
                  <div className="h-6">
                    {selectedFile && (
                      <p
                        className="mt-1 max-w-48 cursor-pointer truncate text-gray-500 hover:max-w-none dark:text-gray-300"
                        title={selectedFile ? selectedFile.name : ''}
                      >
                        {selectedFile.name.length > 20
                          ? selectedFile.name.substring(0, 20) + '...'
                          : selectedFile.name}
                      </p>
                    )}
                  </div>
                </div>
                <div className="mt-10 flex flex-row items-end">
                  {isUploadButtonVisible ? (
                    <button
                      type="submit"
                      className="btn absolute rounded-md border border-gray-600 px-3 py-2 transition-all hover:bg-gray-100 dark:bg-gray-500 dark:hover:bg-gray-600"
                    >
                      Upload
                    </button>
                  ) : null}
                </div>
              </div>
              <div className="absolute bottom-16 right-32 p-2 text-sm text-gray-500 dark:text-gray-500">
                Max file size: 10MB
              </div>
            </div>
          </form>
        </FileModal>
      )}
      {isDocuSignModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="relative w-full max-w-md rounded-lg bg-white p-6 shadow-lg dark:bg-stone-800">
            <button
              onClick={closeDocuSignModal}
              className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-100"
            >
              <BiX className="h-6 w-6" />
            </button>
            <h2 className="mb-4 text-lg font-semibold text-gray-700 dark:text-gray-200">
              Send E-Sign
            </h2>
            <div className="mb-4">
              <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                Document Type
              </label>
              <select
                value={selectedDocTypeId}
                onChange={handleDocTypeChange}
                className="w-[400px] max-w-full rounded-md border border-gray-300 bg-gray-100 py-2.5 px-2 text-gray-700 dark:border-stone-700 dark:bg-stone-700 dark:text-gray-300"
              >
                <option value="">Select Doc Type</option>
                {fileTypes
                  .filter((type) => type.signatureRequired)
                  .map((type) => (
                    <option value={type.id} key={type.id}>
                      {type.type}
                    </option>
                  ))}
              </select>
            </div>
            {selectedDocTypeId && (
              <>
                {loadingSignatories ? (
                  <div className="text-center text-gray-500 dark:text-gray-300">
                    Loading signatory details...
                  </div>
                ) : (
                  <>
                    <div className="mb-4">
                      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                        Agent
                      </h3>
                      {agentSignatory ? (
                        agentSignatory.canOverride ? (
                          <div className="flex flex-wrap gap-4">
                            <div className="w-48 max-w-full">
                              <label className="block text-xs text-gray-600 dark:text-gray-400">
                                Name
                              </label>
                              <input
                                type="text"
                                value={agentSignatory.name}
                                onChange={(e) =>
                                  handleAgentChange('name', e.target.value)
                                }
                                className="w-full rounded-md border border-gray-300 bg-gray-100 py-2 px-2 text-gray-700 dark:border-stone-700 dark:bg-stone-700 dark:text-gray-300"
                                placeholder="Enter name"
                              />
                            </div>
                            <div className="w-48 max-w-full">
                              <label className="block text-xs text-gray-600 dark:text-gray-400">
                                Email
                              </label>
                              <input
                                type="email"
                                value={agentSignatory.email}
                                onChange={(e) =>
                                  handleAgentChange('email', e.target.value)
                                }
                                className="w-full rounded-md border border-gray-300 bg-gray-100 py-2 px-2 text-gray-700 dark:border-stone-700 dark:bg-stone-700 dark:text-gray-300"
                                placeholder="Enter email"
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            <p>Name: {agentSignatory.name}</p>
                            <p>Email: {agentSignatory.email}</p>
                          </div>
                        )
                      ) : (
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          No Agent details available.
                        </div>
                      )}
                    </div>
                    <div className="mb-6">
                      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                        Broker
                      </h3>
                      {brokerSignatory ? (
                        brokerSignatory.canOverride ? (
                          <div className="flex flex-wrap gap-4 mb-4">
                            <div className="w-48 max-w-full">
                              <label className="block text-xs text-gray-600 dark:text-gray-400">
                                Name
                              </label>
                              <input
                                type="text"
                                value={brokerSignatory.name}
                                onChange={(e) =>
                                  handleBrokerChange('name', e.target.value)
                                }
                                className="w-full rounded-md border border-gray-300 bg-gray-100 py-2 px-2 text-gray-700 dark:border-stone-700 dark:bg-stone-700 dark:text-gray-300"
                                placeholder="Enter name"
                              />
                            </div>
                            <div className="w-48 max-w-full">
                              <label className="block text-xs text-gray-600 dark:text-gray-400">
                                Email
                              </label>
                              <input
                                type="email"
                                value={brokerSignatory.email}
                                onChange={(e) =>
                                  handleBrokerChange('email', e.target.value)
                                }
                                className="w-full rounded-md border border-gray-300 bg-gray-100 py-2 px-2 text-gray-700 dark:border-stone-700 dark:bg-stone-700 dark:text-gray-300"
                                placeholder="Enter email"
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            <p>Name: {brokerSignatory.name}</p>
                            <p>Email: {brokerSignatory.email}</p>
                          </div>
                        )
                      ) : (
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          No Broker details available.
                        </div>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
            <div className="flex justify-end">
              <button
                onClick={handleSendDocusign}
                disabled={!selectedDocTypeId}
                className={`flex items-center rounded bg-blue-600 px-4 py-2 text-white font-semibold transition-colors duration-200 hover:bg-blue-700 dark:bg-indigo-600 dark:hover:bg-indigo-700 ${
                  !selectedDocTypeId ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <BiSend className="mr-2" />
                Send
              </button>
            </div>
          </div>
        </div>
      )}
      {showDeleteModal && (
        <DeleteModal
          title="Are you sure?"
          onCancel={() => setShowDeleteModal(false)}
          onDelete={() => handleDeleteConfirmation(fileIdToDelete)}
        />
      )}
      {showConfirmModal && (
        <ConfirmModal
          title="Are you sure this document is complete?"
          onCancel={closeCompletedModal}
          onConfirm={() => handleConfirmation(fileIdToConfirm)}
        />
      )}
      {showDocuSignConfirmModal && (
        <ConfirmModal
          title="Send DocuSign"
          onCancel={closeDocuSignConfirmModal}
          onConfirm={confirmSendDocusign}
        >
          <p>This will send an SAF for electronic signature. Would you like to continue?</p>
        </ConfirmModal>
      )}
      {showVoidModal && (
        <ConfirmModal
          title="Are you sure you want to void this document?"
          onCancel={closeVoidModal}
          onConfirm={handleVoidConfirmation}
        >
          <textarea
            value={voidReason}
            onChange={(e) => setVoidReason(e.target.value)}
            className="h-24 w-full rounded-lg border p-2 text-gray-700 dark:border-stone-700 dark:bg-stone-700 dark:text-gray-300"
            placeholder="Reason for voiding"
            rows="4"
            required
          />
        </ConfirmModal>
      )}
    </div>
  );
}